import React, { useMemo } from 'react';
import { useWindowSize } from '../../hooks/useWindowSize.ts';

export interface IMaterialRequestBreakdown {
	cuts: ICut[];
	reserved: boolean;
	bar_length: number;
	cut_summary: { [key: string]: number };
	total_length_used: number;
	vendor_part_number: string;
	custom_cut?: boolean;
}

export interface ICut {
	cut: number;
	order_line_item: string;
	vendor_part_number: string;
}

interface Props {
	data: IMaterialRequestBreakdown[];
	item_references?: Map<string, string>;
}

const colors = [
	"bg-blue-500",
	"bg-green-500",
	"bg-yellow-500",
	"bg-red-500",
	"bg-pink-500",
	"bg-purple-500",
	"bg-orange-500",
	"bg-teal-500",
];

const getColorForKey = (key: string) => {
	const hash = [...key].reduce((acc, char) => acc + char.charCodeAt(0), 0);
	return colors[hash % colors.length];
};


const getMaxBarLength = (bars: IMaterialRequestBreakdown[]) =>
	Math.max(...bars.map(bar => bar.bar_length));

export const BarVisualizer: React.FC<Props> = ({ data, item_references }) => {


	const { width } = useWindowSize();

	const screenWidth = width ? width : 800;

	const MAX_BAR_WIDTH_PX = screenWidth / 1.7;


	const maxBarLength = getMaxBarLength(data);

	const totalReservedLength = data.reduce(
		(sum, bar) => sum + bar.total_length_used,
		0
	);

	// Aggregate total material used per order_line_item
	const materialPerItemMap = new Map<string, number>();

	data.forEach(bar => {
		bar.cuts.forEach(cut => {
			const current = materialPerItemMap.get(cut.order_line_item) || 0;
			materialPerItemMap.set(cut.order_line_item, current + cut.cut);
		});
	});



	return (
		<div className="space-y-6">
			<div className="mb-4">
				<p className="text-lg font-semibold text-gray-800">
					Total Material Reserved:{" "}
					<span className="text-blue-600">{totalReservedLength.toFixed(2)} in</span>
				</p>
			</div>

			{/* Legend with material per order_line_item */}
			<div className="flex flex-wrap gap-4 mb-4">
				{Array.from(materialPerItemMap.entries()).map(([ref, total]) => {
					const color = getColorForKey(ref);
					return (
						<div key={ref} className="flex items-center gap-2">
							<div className={`w-4 h-4 ${color} rounded`} />
							<span className="text-sm text-gray-800">
								<span className={"font-bold"}>{item_references?.get(ref) ?? ref}:</span> <span className={"font-italic"}>({total.toFixed(2)} IN)</span>
							</span>
						</div>
					);
				})}
			</div>

			{data.map((bar, index) => {
				const barWidthPx = (bar.bar_length / maxBarLength) * MAX_BAR_WIDTH_PX;

				return (
					<div key={index}>
						<p className="font-bold mb-1">
							Bar #{index + 1} ({bar.vendor_part_number}) – Used:{" "}
							{bar.total_length_used} / {bar.bar_length} in
							{bar.custom_cut && (
								<span className="ml-2 text-sm text-red-500">(Custom Cut)</span>
							)}
						</p>
						<div
							className="h-6 border border-gray-300 rounded overflow-hidden flex"
							style={{ width: `${barWidthPx}px` }}
						>
							{bar.cuts.map((cut, idx) => {
								const widthPercent = (cut.cut / bar.bar_length) * 100;
								const color = getColorForKey(cut.order_line_item);
								return (
									<div
										key={idx}
										title={`Item ${cut.order_line_item}: ${cut.cut} in`}
										className={`${color} h-full border-r border-white`}
										style={{ width: `${widthPercent}%` }}
									/>
								);
							})}

							{/* Scrap */}
							{bar.bar_length - bar.total_length_used > 0 && (
								<div
									className="bg-gray-300 h-full"
									style={{
										width: `${
											((bar.bar_length - bar.total_length_used) / bar.bar_length) * 100
										}%`,
									}}
									title={`Scrap: ${bar.bar_length - bar.total_length_used} in`}
								/>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
};
